import React from 'react';
import { SafeAreaProvider } from 'react-native-safe-area-context';

import { NavigationProp, ParamListBase, RouteProp } from '@react-navigation/native';
import { createNativeStackNavigator } from '@react-navigation/native-stack';

import { AcknowledgementsStep } from '@/feature/kyc/screens/Acknowledgements/AcknowledgementsStep';
import { DefaultProductOnboarding } from '@/feature/kyc/screens/DefaultProductOnboarding/DefaultProductOnboarding';
import { DobReview } from '@/feature/kyc/screens/Dob/DobReview';
import { DobStep } from '@/feature/kyc/screens/Dob/DobStep';
import { FirstLastNameReview } from '@/feature/kyc/screens/FirstLastName/FirstLastNameReview';
import { FirstLastNameStep } from '@/feature/kyc/screens/FirstLastName/FirstLastNameStep';
import { HomeAddressReview } from '@/feature/kyc/screens/HomeAddress/HomeAddressReview';
import { HomeAddressStep } from '@/feature/kyc/screens/HomeAddress/HomeAddressStep';
import { IdpvWebView } from '@/feature/kyc/screens/Idpv/IdpvWebView';
import { ScanId } from '@/feature/kyc/screens/Idpv/ScanId';
import { getPreviousRouteName } from '@/feature/kyc/screens/Idpv/utils';
import { KycSelector } from '@/feature/kyc/screens/KycSelector';
import { ReviewPersonalInfo } from '@/feature/kyc/screens/ReviewPersonalInfo';
import { SsnRetry } from '@/feature/kyc/screens/Ssn/SsnRetry';
import { SsnStep } from '@/feature/kyc/screens/Ssn/SsnStep';
import { TrustlyId } from '@/feature/kyc/screens/Trustly/TrustlyId';
import { TrustlyKycStatus } from '@/feature/kyc/screens/Trustly/TrustlyKycStatus';
import { TrustlyNumpad } from '@/feature/kyc/screens/Trustly/TrustlyNumpad';
import { TrustlyWebview } from '@/feature/kyc/screens/Trustly/TrustlyWebview';
import { VerificationFailed } from '@/feature/kyc/screens/Verification/VerificationFailed';
import { VerificationSuccess } from '@/feature/kyc/screens/Verification/VerificationSuccess';
import { VerifyingIdentity } from '@/feature/kyc/screens/Verification/VerifyingIdentity';
import { VerifyingIdpv } from '@/feature/kyc/screens/Verification/VerifyingIdpv';
import { VerificationSelector } from '@/feature/withdraw/screens/VerificationSelector';
import { KycStackParamList, RootStackParamList } from '@/navigation/types';
import { common } from '@/styles/styles';

import { screenPresets } from '../screen-presets';

const Stack = createNativeStackNavigator<KycStackParamList>();

const isOnboardingRoute = (name: string) => name === 'Splash' || name === 'Login' || name === 'Register';

// onboarding KYC will replace Splash/Login/Register with Lobby
// so it needs transparentFullScreenCover
export const getKycStackOptions = ({
    navigation,
    route,
}: {
    navigation: NavigationProp<ParamListBase>;
    route: RouteProp<RootStackParamList, 'KycStack'>;
}) =>
    isOnboardingRoute(getPreviousRouteName(navigation, route))
        ? screenPresets.transparentFullScreenCover
        : screenPresets.fullScreenCover;

export const KycStack = () => (
    <SafeAreaProvider style={common.flex}>
        <Stack.Navigator screenOptions={screenPresets.modalStack}>
            {/* IDComply flow */}
            <Stack.Screen name="KycSelector" component={KycSelector} />
            <Stack.Screen name="KycSsn" component={SsnStep} />
            <Stack.Screen name="KycFirstLastName" component={FirstLastNameStep} />
            <Stack.Screen name="KycDob" component={DobStep} />
            <Stack.Screen name="KycHomeAddress" component={HomeAddressStep} />
            <Stack.Screen name="KycAcknowledgements" component={AcknowledgementsStep} />
            {/* IDComply retry flow */}
            <Stack.Screen name="KycSsnRetry" component={SsnRetry} />
            <Stack.Screen name="KycReviewPersonalInfo" component={ReviewPersonalInfo} />
            <Stack.Screen name="KycAcknowledgementsRetry" component={AcknowledgementsStep} />
            <Stack.Screen
                name="KycFirstLastNameReview"
                component={FirstLastNameReview}
                options={screenPresets.sheet()}
            />
            <Stack.Screen name="KycDobReview" component={DobReview} options={screenPresets.sheet()} />
            <Stack.Screen name="KycHomeAddressReview" component={HomeAddressReview} options={screenPresets.sheet()} />
            {/* verification */}
            <Stack.Screen
                name="KycVerificationFailed"
                component={VerificationFailed}
                options={screenPresets.gestureDisabled}
            />
            <Stack.Screen
                name="KycVerifyingIdentity"
                component={VerifyingIdentity}
                options={screenPresets.gestureDisabled}
            />
            <Stack.Screen
                name="KycVerificationSuccess"
                component={VerificationSuccess}
                options={{ ...screenPresets.gestureDisabled, ...screenPresets.noAnimation }}
            />
            <Stack.Screen
                name="DefaultProductOnboarding"
                component={DefaultProductOnboarding}
                options={screenPresets.gestureDisabled}
            />
            {/* IDPV */}
            <Stack.Screen name="KycScanId" component={ScanId} />
            <Stack.Screen name="KycIdpvWebView" component={IdpvWebView} />
            <Stack.Screen name="KycVerifyingIdpv" component={VerifyingIdpv} options={screenPresets.gestureDisabled} />
            {/* Trustly */}
            <Stack.Screen name="TrustlyId" component={TrustlyId} />
            <Stack.Screen name="TrustlyNumpad" component={TrustlyNumpad} />
            <Stack.Screen name="TrustlyWebview" component={TrustlyWebview} options={screenPresets.gestureDisabled} />
            <Stack.Screen name="TrustlyKycStatusModal" component={TrustlyKycStatus} />
            {/* withdraw verification */}
            <Stack.Screen name="VerificationSelector" component={VerificationSelector} />
        </Stack.Navigator>
    </SafeAreaProvider>
);
